<template>
    <div class="pb-5">
        <div class="card-header d-flex justify-content-between align-items-center">
            <div class="card-header__action">
                <i v-if="isEditing" class="pi pi-times" @click="cancelEdit"/>
                <i v-else class="pi pi-times invisible"/>
            </div>
            <div class="card-header__title">
                {{ $t("AppVehicleInfoGeneral.Informações gerais") }}
            </div>
            <div class="card-header__action">
                <i v-if="!isEditing" class="pi pi-pencil" @click="toggleEdit"/>
                <i v-else class="pi pi-check" @click="saveEdit"/>
            </div>
        </div>
        <div class="row pb-1" :class="[isEditing ? 'pt-sm-2 pt-1' : 'pt-sm-4 pt-2']">
            <div class="col-sm-6 col-12">
                <div class="d-flex flex-column align-items-center mt-1">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Chassis') }}
                    </div>
                    <div class="field-value d-flex flex-column align-items-center">
                        <InputText v-if="isEditing" style="width: 80%" v-model="identificationNumber"/>
                        <div v-else-if="vehicle.identification_number">
                            {{ vehicle.identification_number }}
                        </div>
                        <div v-else>
                            N/D
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column align-items-center mt-3">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Fabricante') }}
                    </div>
                    <div v-if="isEditing" class="field-value d-flex flex-column align-items-center">
                        <Dropdown v-model="selectedManufacturer"
                                  :options="manufacturers"
                                  optionLabel="name" :dataKey="'id'" filter :filterPlaceholder="$t('AppVehicleInfoGeneral.Procurar')"
                                  @change="updateModels"
                                  :placeholder="$t('AppVehicleInfoGeneral.Selecione o fabricante')">
                        </Dropdown>
                    </div>
                    <div v-else class="field-value d-flex flex-column align-items-center">
                        <div v-if="vehicleManufacturer">
                            {{ vehicleManufacturer.name }}
                        </div>
                        <div v-else>
                            N/D
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column align-items-center mt-3">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Frota') }}
                    </div>
                    <div class="field-value d-flex flex-column align-items-center">
                        <InputText v-if="isEditing" style="width: 80%" v-model="fleet"/>
                        <div v-else>
                            <div v-if="vehicle.identification">
                                {{ vehicle.identification }}
                            </div>
                            <div v-else>
                                N/D
                            </div>
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column align-items-center mt-3">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Ano de fabricação') }}
                    </div>
                    <div class="field-value d-flex flex-column align-items-center">
                        <InputNumber v-if="isEditing" style="width: 50%" :useGrouping="false"
                                     v-model="manufacturingYear"/>
                        <div v-else>
                            <div v-if="vehicle.year">
                                {{ vehicle.year }}
                            </div>
                            <div v-else>
                                N/D
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-sm-6 col-12">
                <div class="d-flex flex-column align-items-center mt-1">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Dispositivo') }}
                    </div>
                    <div class="field-value">
                        <div v-if="vehicle.mac">
                            {{ vehicle.mac }}
                        </div>
                        <div v-else>
                            N/D
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column align-items-center mt-3">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Modelo') }}
                    </div>
                    <div v-if="isEditing" class="field-value d-flex flex-column align-items-center">
                        <Dropdown v-model="selectedModel" :options="models" optionLabel="name"
                                  :disabled="!selectedManufacturer" :dataKey="'id'" filter :filterPlaceholder="$t('AppVehicleInfoGeneral.Procurar')"
                                  :placeholder="$t('AppVehicleInfoGeneral.Selecione o modelo')">
                        </Dropdown>
                    </div>
                    <div v-else class="field-value d-flex flex-column align-items-center">
                        <div v-if="vehicleModel">
                            {{ vehicleModel.name }}
                        </div>
                        <div v-else>
                            N/D
                        </div>
                    </div>
                </div>
                <div class="d-flex flex-column align-items-center mt-3">
                    <div class="field-title">
                        {{ $t('AppVehicleInfoGeneral.Tipo') }}
                    </div>
                    <div class="field-value">
                        <div v-if="isEditing">
                            <div>
                                <div v-if="selectedModel">
                                    {{ selectedModel.display_vehicle_type }}
                                </div>
                                <div v-else>
                                    N/D
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div v-if="vehicleModel">
                                {{ vehicleModel.display_vehicle_type }}
                            </div>
                            <div v-else>
                                N/D
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="processing && !isEditing" class="text-center pt-3 pt-sm-5 mx-3">
            <div v-if="processing.status.includes('SENDING')" style="color:#b31217">
                {{ $t('AppVehicleInfoGeneral.Atenção, as informações do veículo podem estar desatualizadas') }}
                <span class="pi pi-question-circle help-style"
                      v-tooltip="$t('AppVehicleInfoGeneral.Um arquivo de configuração criado em') + formatSentDate(processing.created_date) +
                       $t('AppVehicleInfoGeneral.está em processamento')"/>
            </div>
        </div>
    </div>
</template>

<script>

import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Tooltip from "primevue/tooltip";
import Checkbox from "primevue/checkbox";
import {VEHICLES_TYPES} from "@/constants/VehiclesConstants";
import dateHourFormat from "@/mixins/DateMixin";

export default {
    mixins: [dateHourFormat],
    data() {
        return {
            isEditing: false,
            saving: false,
            selectedManufacturer: null,
            selectedModel: null,
            identificationNumber: null,
            fleet: null,
            manufacturingYear: null,
            vehicleTypes: VEHICLES_TYPES.slice(1)
        }
    },
    props: {
        vehicle: {
            type: Object,
            required: true
        },
        vehicleManufacturer: {
            type: Object
        },
        vehicleModel: {
            type: Object
        },
        manufacturers: {
            type: Array,
            required: true
        },
        models: {
            type: Array,
            required: true
        },
        processing: {
            type: Object
        },
    },
    methods: {
        formatSentDate(fileDate) {
            let dateHour = this.dateHourFormat(fileDate)
            return dateHour.split(' ')[0] +  this.$t('AppVehicleInfoGeneral.às') + dateHour.split(' ')[1]
        },
        handleCheckbox() {
            this.selectedManufacturer = null
            this.selectedModel = null
        },
        toggleEdit() {
            this.isEditing = true
            this.selectedManufacturer = this.vehicleManufacturer
            this.selectedModel = this.vehicleModel
            this.fleet = this.vehicle.identification
            this.identificationNumber = this.vehicle.identification_number
            this.manufacturingYear = this.vehicle.year
        },
        cancelEdit() {
            this.isEditing = false
        },
        saveEdit() {
            if (this.invalidInput()) {
                return
            }

            this.saving = true
            let params = {device_id: this.vehicle.device_id};
            let newVehicle = {
                _035: this.identificationNumber,
                _752: this.fleet,
                _843: this.vehicle._843,
                _844: this.vehicle._844,
                mac_address: this.vehicle.mac,
                source_file: this.vehicle.source_file,
                _128: this.manufacturingYear,
            }
            newVehicle._027 = this.selectedManufacturer.id
            newVehicle._029 = this.selectedModel.vehicle_type.toString()
            newVehicle._030 = this.selectedModel.id
            this.$emit('requestSaveVehicle', params, newVehicle)
        },
        isChassisTest(chassis) {
            return chassis.includes('NOCHASSIS') || chassis.includes('IOTAG') || chassis.includes('TEST');
        },
        invalidInput() {
            if (this.isChassisTest(this.identificationNumber)) {
                this.$toast.add({
                    severity: 'error',
                    summary: this.$t('AppVehicleInfoGeneral.Chassis inválido'),
                    life: 5000
                });
                return true
            }

            if (!this.identificationNumber) {
                this.$toast.add({
                    severity: 'error',
                    summary: this.$t('AppVehicleInfoGeneral.É necessário definir o chassis'),
                    life: 5000
                });
                return true
            }

            if (!this.fleet) {
                this.$toast.add({
                    severity: 'error',
                    summary: this.$t('AppVehicleInfoGeneral.É necessário definir a frota'),
                    life: 5000
                });
                return true
            }

            if (!this.vehicle.device_id && this.vehicle.identification_number != this.identificationNumber) {
                this.$toast.add({
                    severity: 'error',
                    summary: this.$t('AppVehicleInfoGeneral.Não é possível alterar o chassis de um veículo sem dispositivo'),
                    life: 5000
                });
                return true
            }

            if (this.manufacturingYear) {
                let maxYear = 2030
                let minYear = 1950
                if (this.manufacturingYear < minYear || this.manufacturingYear > maxYear) {
                    this.$toast.add({
                        severity: 'error',
                        summary: this.$t('AppVehicleInfoGeneral.Ano inválido', {minYear, maxYear}),
                        life: 5000
                    });
                    return true
                }
            }

            if (!this.selectedManufacturer) {
                this.$toast.add({
                    severity: 'error',
                    summary: this.$t('AppVehicleInfoGeneral.É necessário selecionar um fabricante'),
                    life: 5000
                });
                return true
            }
            if (!this.selectedModel) {
                this.$toast.add({
                    severity: 'error',
                    summary: this.$t('AppVehicleInfoGeneral.É necessário selecionar um modelo'),
                    life: 5000
                });
                return true
            }

            return false
        },
        updateModels() {
            this.selectedModel = null
            this.$emit('getModels', this.selectedManufacturer.id, true)
        }
    },
    watch: {
        vehicleModel: {
            deep: true,
            handler() {
                if (this.saving) {
                    this.isEditing = false
                    this.saving = false
                }
            }
        }
    },
    directives: {
        'tooltip': Tooltip
    },
    components: {
        InputText, Dropdown, InputNumber, Checkbox
    }
}
</script>

<style scoped>

.card-header {
    padding: 16px 18px;
    border-bottom: 1px solid #f0f2f3;
    background: transparent;
}

.card-header__title {
    font-size: 1.05rem;
    font-weight: 600;
    color: #3a3a3a;
}

.card-header__action .pi {
    font-size: 18px;
    color: #8a949c;
    cursor: pointer;
    transition: color .15s ease;
}

.card-header__action .pi:hover {
    color: #3a3a3a;
}

.card-header__action .pi.invisible {
    cursor: auto;
}

.field-title {
    color: #8a949c;
    font-size: .82rem;
    text-transform: uppercase;
    letter-spacing: .03em;
}

.field-value {
    color: #2b2b2b;
    font-weight: 600;
    font-size: 1.02rem;
}
</style>
